import React, { useEffect, useState } from 'react';
import CustomTable from '../../components/common/Table.tsx';
import Searchbar from '../../components/common/Searchbar.tsx';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store.ts';
import Confirmation from '../../components/common/Confirmation.tsx';
import { notification, Pagination } from 'antd';
import { useLocation } from 'react-router-dom';
import { IAssignmentReq, IAssignmentRes, IVendorRes } from '../../type/type.ts';
import { capitalizeFirstLetter } from '../../utils/util.service.ts';
import { setBreadcrumbs } from '../../slices/BreadcrumbSlice.ts';
import CenterModal from '../../components/common/Centermodal.tsx';
import { getAllAssignments, assignVendor, removeCustomerRequests, setOffset, setLimit, setSortField, setSortOrder } from '../../slices/AssignmentSlice.ts';
import AssignVendor from '../../components/sheet/AssignVendor.tsx';

const Assignments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const { assignments, loading, dataCount, offset, limit } = useSelector((state: RootState) => state.assignmentSlice);
  const [selectedAssignment, setSelectedAssignment] = useState<IAssignmentReq | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("Do you want to submit the form?");
  const [clientToDelete, setClientToDelete] = useState<IAssignmentRes | null>(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null,
  });

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<IVendorRes | null>(null);

  const handleAssignClick = (record: IAssignmentRes) => {
    const assignmentData: IAssignmentReq = {
      s_no: 0,
      request_id: record.id,
      customer_id: record.customer_id,
      customer_name: record.customer_name || '',
      vendor_id: record.vendor_id || 0,
      vendor_name: '',
      service_id: record.service_id,
      service_name: record.service_name,
      sub_service_id: record.sub_service_id,
      sub_service_name: record.sub_service_name,
      request_date: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'pending'
    };
    setSelectedAssignment(assignmentData);
    setSelectedServiceId(record.service_id);
    setIsAssignModalOpen(true);
  };

  const handleAssignModalClose = () => {
    setIsAssignModalOpen(false);
    setSelectedServiceId(null);
    setSelectedVendor(null);
  };

  const handleAssignModalSubmit = async () => {
    if (!selectedAssignment || !selectedVendor) {
      showNotification("error", "Please select a vendor first");
      return;
    }

    try {
      await dispatch(assignVendor({
        assignmentId: selectedAssignment.request_id,
        vendorId: selectedVendor.id
      })).unwrap();
      
      showNotification("success", "Vendor assigned successfully");
      setIsAssignModalOpen(false);
      setSelectedVendor(null);
      // Refresh assignments
      dispatch(getAllAssignments({
        offset: offset,
        limit: limit,
        sortField: sortConfig.key ?? null,
        sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
        searchInput: query
      }));
    } catch (error: any) {
      showNotification("error", error.message || "Failed to assign vendor");
    }
  };

  const handleDeleteClick = (record: IAssignmentRes) => {
    if (!record || !record.id) {
      showNotification("error", "Invalid record selected for deletion");
      return;
    }
    setClientToDelete(record);
    setConfirmationMessage("Are you sure you want to delete this request?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete || !clientToDelete.id) {
      showNotification("error", "Invalid record selected for deletion");
      setIsDeleteConfirmationOpen(false);
      setClientToDelete(null);
      return;
    }

    try {
      await dispatch(removeCustomerRequests(clientToDelete.id)).unwrap();
      showNotification("success", "Request deleted successfully");
      // Refresh the assignments list
      dispatch(getAllAssignments({
        offset: offset,
        limit: limit,
        sortField: sortConfig.key ?? null,
        sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
        searchInput: query
      }));
    } catch (error: any) {
      showNotification("error", error.message || "Failed to delete request");
    }
    setIsDeleteConfirmationOpen(false);
    setClientToDelete(null);
  };

  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {
    dispatch(setOffset(page - 1));
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};
    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || null,
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));
    dispatch(setOffset(offset));
  };

  useEffect(() => {
    dispatch(getAllAssignments({
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
      searchInput: query
    }));
  }, [dispatch, offset, limit, sortConfig, query]);

  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "Assignments",
        path: '/assignments'
      }
    ]));
  }, []);

  const columns = [
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">S.No</span>,
      dataIndex: "s_no",
      key: "serialNumber",
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 50,
    },
    {
      title: <span className="text-[#6D6D6D]">Client Name</span>,
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "customer_name" ? sortConfig.order : null,
      render: (name: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(name)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Care Category</span>,
      dataIndex: 'service_name',
      key: 'serviceName',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "service_name" ? sortConfig.order : null,
      render: (serviceName: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(serviceName)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Care Type</span>,
      dataIndex: 'sub_service_name',
      key: 'subServiceName',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "sub_service_name" ? sortConfig.order : null,
      render: (subServiceName: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(subServiceName)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Request Date</span>,
      dataIndex: 'request_date',
      key: 'requestDate',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "request_date" ? sortConfig.order : null,
      render: (createdAt: string) => <span className="text-[#0A0A0A] font-medium text-sm">{new Date(createdAt).toLocaleDateString()}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Assign</span>,
      dataIndex: 'Assign',
      key: 'Assign',
      sorter: false,
      render: (_: any, record: IAssignmentRes) => (
        <div className="flex items-center gap-8">
          <button 
            onClick={() => handleAssignClick(record)}
            className='bg-[#000000] text-white px-4 py-2 rounded-md'
          >
            {record.vendor_id ? 'Reassign' : 'Assign'}
          </button>
          <img
            src="/client/delete.svg"
            alt="Delete"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }
  ];

  return (
    <>
      <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
        <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A] ">Assignments</p>
        <div className="flex justify-end gap-3 ">
          <div className="flex justify-end ">
            <Searchbar search={query} onSearch={handleSearch} />
          </div>
        </div>
      </div>
      <div>
        <CustomTable
          columns={columns}
          dataSource={assignments}
          loading={loading}
          onChange={handleSortChange}
          scrollHeight={800}
        />
      </div>
      <div className="sticky bottom-0 flex justify-end py-4">
        <Pagination
          total={dataCount}
          showSizeChanger={false}
          onChange={handlePaginationChange}
          current={offset + 1}
          pageSize={limit}
        />
      </div>
      <CenterModal
        isOpen={isAssignModalOpen}
        setOpen={setIsAssignModalOpen}
        title="Assign Vendor"
        onSubmit={handleAssignModalSubmit}
        onCancel={handleAssignModalClose}
        submitLabel="Done"
        cancelLabel="Cancel"
      >
        {selectedServiceId && (
          <AssignVendor
            serviceId={selectedServiceId}
            isOpen={isAssignModalOpen}
            onVendorSelect={(vendor) => {
              setSelectedVendor(vendor);
              notification.success({
                message: 'Vendor Selected',
                description: `${vendor.vendor_name} has been selected as the vendor.`,
                placement: 'top'
              });
            }}
          />
        )}
      </CenterModal>
      <Confirmation
        label="Delete Confirmation"
        message={confirmationMessage}
        isOpen={isDeleteConfirmationOpen}
        onClose={() => {
          setIsDeleteConfirmationOpen(false);
          setClientToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmButtonLabel="Delete"
      />
    </>
  );
};

export default Assignments;